import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, Eye, Send, CheckCircle, XCircle, AlertCircle, Calendar, DollarSign, FileText, TrendingUp } from 'lucide-react';
import { invoicesAPI, Invoice, InvoicesResponse, GenerateInvoicesResponse } from '../../services/invoicesAPI';

const InvoiceManagement: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [yearFilter, setYearFilter] = useState<string>(new Date().getFullYear().toString());
  const [monthFilter, setMonthFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generatingInvoices, setGeneratingInvoices] = useState(false);
  const [generateResult, setGenerateResult] = useState<GenerateInvoicesResponse | null>(null);

  useEffect(() => {
    loadInvoices();
  }, [currentPage, statusFilter, yearFilter, monthFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, yearFilter, monthFilter]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit: 10,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (yearFilter) {
        params.year = parseInt(yearFilter);
      }
      if (monthFilter) {
        params.month = parseInt(monthFilter);
      }

      const response = await invoicesAPI.getAllInvoices(params);
      setInvoices(response.data.invoices);
      setTotalPages(response.data.pagination.pages);
    } catch (error) {
      console.error('Failed to load invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMonthlyInvoices = async (year: number, month: number) => {
    try {
      setGeneratingInvoices(true);
      const response = await invoicesAPI.generateMonthlyInvoices(year, month);
      setGenerateResult(response.data);
      setShowGenerateModal(false);
      loadInvoices();
    } catch (error) {
      console.error('Failed to generate invoices:', error);
    } finally {
      setGeneratingInvoices(false);
    }
  };

  const handleUpdateInvoiceStatus = async (invoiceId: string, status: string, notes?: string) => {
    try {
      await invoicesAPI.updateInvoiceStatus(invoiceId, status, notes);
      loadInvoices();
      setSelectedInvoice(null);
    } catch (error) {
      console.error('Failed to update invoice status:', error);
    }
  };

  const handleMarkAsPaid = async (invoiceId: string, paymentMethod: string) => {
    try {
      await invoicesAPI.markInvoiceAsPaid(invoiceId, paymentMethod);
      loadInvoices();
      setSelectedInvoice(null);
    } catch (error) {
      console.error('Failed to mark invoice as paid:', error);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-700', icon: FileText, label: 'Draft' },
      sent: { color: 'bg-blue-100 text-blue-700', icon: Send, label: 'Sent' },
      paid: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Paid' },
      overdue: { color: 'bg-red-100 text-red-700', icon: AlertCircle, label: 'Overdue' },
      cancelled: { color: 'bg-gray-100 text-gray-700', icon: XCircle, label: 'Cancelled' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const getStatusColor = (status: string) => {
    const colors = {
      draft: 'text-gray-600',
      sent: 'text-blue-600',
      paid: 'text-green-600',
      overdue: 'text-red-600',
      cancelled: 'text-gray-600',
    };
    return colors[status as keyof typeof colors] || 'text-gray-600';
  };

  const filteredInvoices = invoices.filter(invoice =>
    invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.siteId.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.billingInfo.companyName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoice Management</h1>
          <p className="text-gray-600">Manage and generate invoices for all sites</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowGenerateModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2"
          >
            <Calendar className="w-4 h-4" />
            <span>Generate Monthly Invoices</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by invoice number, site name, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Months</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                <option key={month} value={month}>{new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Generate Result Summary */}
      {generateResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <h3 className="text-green-800 font-medium">Invoice Generation Complete</h3>
              <p className="text-green-600 text-sm">
                Generated {generateResult.summary.totalInvoices} invoices totaling {formatCurrency(generateResult.summary.totalAmount)}
                {generateResult.summary.errors > 0 && ` (${generateResult.summary.errors} errors)`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Site
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInvoices.map((invoice) => (
                <tr key={invoice._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{invoice.invoiceNumber}</div>
                      <div className="text-sm text-gray-500">Issued: {formatDate(invoice.issueDate)}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{invoice.siteId.name}</div>
                      <div className="text-sm text-gray-500">{invoice.billingInfo.companyName}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(invoice.billingPeriod.startDate)} - {formatDate(invoice.billingPeriod.endDate)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(invoice.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className={`font-medium ${getStatusColor(invoice.status)}`}>
                        {formatCurrency(invoice.totalAmount, invoice.currency)}
                      </div>
                      {invoice.balanceAmount !== invoice.totalAmount && (
                        <div className="text-xs text-gray-500">
                          Paid: {formatCurrency(invoice.paidAmount, invoice.currency)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(invoice.dueDate)}
                      {invoice.status === 'overdue' && (
                        <div className="text-xs text-red-600">Overdue</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setSelectedInvoice(invoice)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      {invoice.status === 'draft' && (
                        <button
                          onClick={() => handleUpdateInvoiceStatus(invoice._id, 'sent')}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                      {invoice.status === 'sent' && (
                        <button
                          onClick={() => handleMarkAsPaid(invoice._id, 'bank_transfer')}
                          className="text-green-600 hover:text-green-900"
                        >
                          <DollarSign className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, filteredInvoices.length)} of {filteredInvoices.length} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Generate Monthly Invoices Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Generate Monthly Invoices</h2>
                <button
                  onClick={() => setShowGenerateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                  <select
                    defaultValue={new Date().getFullYear()}
                    id="generate-year"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {Array.from({ length: 3 }, (_, i) => new Date().getFullYear() + i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
                  <select
                    defaultValue={new Date().getMonth() + 1}
                    id="generate-month"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <option key={month} value={month}>
                        {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setShowGenerateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const year = parseInt((document.getElementById('generate-year') as HTMLSelectElement).value);
                      const month = parseInt((document.getElementById('generate-month') as HTMLSelectElement).value);
                      handleGenerateMonthlyInvoices(year, month);
                    }}
                    disabled={generatingInvoices}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {generatingInvoices ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Generating...
                      </>
                    ) : (
                      'Generate Invoices'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Invoice {selectedInvoice.invoiceNumber}</h2>
                  <p className="text-gray-600">{selectedInvoice.billingInfo.companyName}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusBadge(selectedInvoice.status)}
                  <button
                    onClick={() => setSelectedInvoice(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Invoice Content */}
              <div className="space-y-6">
                {/* Header Info */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Bill To</h3>
                    <div className="text-sm text-gray-900">
                      <div className="font-medium">{selectedInvoice.billingInfo.companyName}</div>
                      <div>{selectedInvoice.billingInfo.contactName}</div>
                      <div>{selectedInvoice.billingInfo.billingAddress.street}</div>
                      <div>{selectedInvoice.billingInfo.billingAddress.city}, {selectedInvoice.billingInfo.billingAddress.postcode}</div>
                      <div>{selectedInvoice.billingInfo.billingAddress.country}</div>
                      <div>{selectedInvoice.billingInfo.contactEmail}</div>
                      {selectedInvoice.billingInfo.vatNumber && (
                        <div>VAT: {selectedInvoice.billingInfo.vatNumber}</div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Invoice Details</h3>
                    <div className="text-sm text-gray-900 space-y-1">
                      <div><strong>Invoice Number:</strong> {selectedInvoice.invoiceNumber}</div>
                      <div><strong>Issue Date:</strong> {formatDate(selectedInvoice.issueDate)}</div>
                      <div><strong>Due Date:</strong> {formatDate(selectedInvoice.dueDate)}</div>
                      <div><strong>Period:</strong> {formatDate(selectedInvoice.billingPeriod.startDate)} - {formatDate(selectedInvoice.billingPeriod.endDate)}</div>
                      <div><strong>Status:</strong> {selectedInvoice.status}</div>
                      {selectedInvoice.paidDate && (
                        <div><strong>Paid Date:</strong> {formatDate(selectedInvoice.paidDate)}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Line Items */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-4">Invoice Items</h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedInvoice.items.map((item, index) => (
                          <tr key={index} className={item.type === 'discount' ? 'bg-red-50' : ''}>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.description}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.quantity}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                              {formatCurrency(item.unitPrice, selectedInvoice.currency)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                              {formatCurrency(item.amount, selectedInvoice.currency)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(selectedInvoice.subtotal, selectedInvoice.currency)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>VAT ({selectedInvoice.taxRate}%):</span>
                      <span>{formatCurrency(selectedInvoice.taxAmount, selectedInvoice.currency)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-medium pt-2 border-t">
                      <span>Total:</span>
                      <span>{formatCurrency(selectedInvoice.totalAmount, selectedInvoice.currency)}</span>
                    </div>
                    {selectedInvoice.paidAmount > 0 && (
                      <>
                        <div className="flex justify-between text-sm text-green-600">
                          <span>Paid:</span>
                          <span>{formatCurrency(selectedInvoice.paidAmount, selectedInvoice.currency)}</span>
                        </div>
                        <div className="flex justify-between text-sm font-medium">
                          <span>Balance:</span>
                          <span>{formatCurrency(selectedInvoice.balanceAmount, selectedInvoice.currency)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-4 pt-4 border-t">
                  {selectedInvoice.status === 'draft' && (
                    <button
                      onClick={() => handleUpdateInvoiceStatus(selectedInvoice._id, 'sent')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                    >
                      <Send className="w-4 h-4" />
                      <span>Send Invoice</span>
                    </button>
                  )}
                  {selectedInvoice.status === 'sent' && (
                    <button
                      onClick={() => handleMarkAsPaid(selectedInvoice._id, 'bank_transfer')}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
                    >
                      <DollarSign className="w-4 h-4" />
                      <span>Mark as Paid</span>
                    </button>
                  )}
                  <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2">
                    <Download className="w-4 h-4" />
                    <span>Download PDF</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceManagement;
